import { EditOutlined } from '@mui/icons-material';
import { useTheme, Stack, Typography, Divider, Grid, Box } from '@mui/material';
import { LabelledText } from 'components/LabelledText/LabelledText';
import { PageSection } from 'components/PageSection/PageSection';
import { ShrinkToIconButton } from 'components/ShrinkToIconButton/ShrinkToIconButton';
import { CalculationDialog } from 'pages/ProjectDashboard/ProjectCalculationsDialog';
import { useTranslation } from 'react-i18next';
import { CalculationDetails, Calculation, Application, CalculationDialogMode } from 'types/domainTypes';
import { getCalculationTypeText, getApplicationText } from '../helpers';
import { ReactElement } from 'react';

interface CalculationDetailsViewProps {
  calculation: Calculation;
  onEditDetails: (calculationDetails: CalculationDetails) => void;
  isEditDetailsDialogOpen: boolean;
  setIsEditDetailsDialogOpen: (isDialogOpen: boolean) => void;
}

export function CalculationDetailsView({
  calculation,
  onEditDetails,
  isEditDetailsDialogOpen,
  setIsEditDetailsDialogOpen,
}: CalculationDetailsViewProps): ReactElement {
  const theme = useTheme();
  const { t } = useTranslation();
  const details = calculation.details;

  const onCalculationSave = async (updatedCalculationDetails: CalculationDetails) => {
    onEditDetails(updatedCalculationDetails);
    setIsEditDetailsDialogOpen(false);
  };

  const handleCloseCalculationDialog = () => {
    setIsEditDetailsDialogOpen(false);
  };

  return (
    <PageSection>
      {isEditDetailsDialogOpen && (
        <CalculationDialog
          onSave={onCalculationSave}
          onClose={handleCloseCalculationDialog}
          calculation={calculation}
          mode={CalculationDialogMode.Edit}
        />
      )}

      <Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack>
            <Typography variant="h6" fontFamily="KingspanBrownTT-Bold">
              {t('calculation-page.label-calculation-details')}
            </Typography>
            <Typography variant="body2" color={theme.palette.grey[500]}>
              {t('common.summary-format', {
                createdBy: details.createdByName ? details.createdByName : details.createdBy,
                modifiedOn: details.modifiedOn,
                modifiedBy: details.modifiedByName ? details.modifiedByName : details.modifiedBy,
              })}
            </Typography>
          </Stack>
          <Box sx={{ flexShrink: 0 }}>
            <ShrinkToIconButton
              buttonText={t('calculation-page.edit-calculation-details-button')}
              icon={<EditOutlined />}
              onClick={() => setIsEditDetailsDialogOpen(true)}
            />
          </Box>
        </Stack>

        <Divider />

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Stack spacing={2}>
              <LabelledText label="calculation.label-type" value={getCalculationTypeText(details.type)} />
            </Stack>
          </Grid>
          <Grid item xs={4}>
            <LabelledText label="calculation.label-application" value={getApplicationText(details.application as Application)} />
          </Grid>
          <Grid item xs={4} display={details.description ? 'inherit' : 'none'}>
            <LabelledText label="calculation-page.label-calculation-description" value={details.description} showNewline />
          </Grid>
        </Grid>
      </Stack>
    </PageSection>
  );
}
